'use client'

import { useEffect } from 'react'
import { isSessionActive, onSessionActiveChange, shouldReloadNow } from '@/lib/pwa/session-activity'

export default function ServiceWorkerRegistration() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return
    }

    // Only register in production
    if (process.env.NODE_ENV !== 'production') return

    // A single-page-app tab keeps running the JS bundle it FIRST loaded — so a
    // shipped fix can look "not applied" to a returning/long-lived tab until a
    // manual hard-refresh (the recurring "still seeing the old UI" reports).
    // When a NEW service worker takes control (a fresh deploy whose sw.js bumped
    // CACHE_VERSION), reload ONCE so the tab runs the new bundle. `hadController`
    // skips the very first install, so a brand-new visitor is never reloaded;
    // `refreshing` guards against a reload loop.
    //
    // The one thing this reload must NOT do is yank the page out from under a
    // student mid-question: a deploy landing during a practice run or an SAT
    // module reloaded the tab and cost them their place (2026-07-22 report).
    // So while a session surface holds the session-active flag we DEFER — the
    // new worker is already in control, so the next full navigation gets fresh
    // assets regardless — and reload the moment the session goes idle.
    const hadController = !!navigator.serviceWorker.controller
    let refreshing = false
    let pendingReload = false

    const reloadNow = () => {
      refreshing = true
      window.location.reload()
    }
    const onControllerChange = () => {
      if (refreshing || !hadController) return
      if (!shouldReloadNow({ hadController, refreshing, sessionActive: isSessionActive() })) {
        // Deferred: either a brand-new install (never reload) or a live
        // session. Only the live-session case should reload later.
        pendingReload = isSessionActive()
        return
      }
      reloadNow()
    }
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)

    // The student finished (or left) their session — safe to pick up the deploy.
    const offSessionChange = onSessionActiveChange(() => {
      if (!pendingReload || refreshing || isSessionActive()) return
      reloadNow()
    })

    let registration: ServiceWorkerRegistration | undefined
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        registration = reg
        // Proactively check for a newer SW on load.
        reg.update().catch(() => {})
      })
      .catch((err) => {
        console.error('Service worker registration failed:', err)
      })

    // Re-check when the tab regains focus, so a deploy that happened while the
    // tab was backgrounded is picked up the next time the student returns.
    const onFocus = () => registration?.update().catch(() => {})
    window.addEventListener('focus', onFocus)

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
      window.removeEventListener('focus', onFocus)
      offSessionChange()
    }
  }, [])

  return null
}
